/**
 * Layout - Root layout wrapper with sidebar + topic bar
 *
 * This is the global layout that wraps all pages. It provides:
 * - The topic bar (top-level, above everything)
 * - The global sidebar (hidden when topic focused — immersive mode)
 * - A flex container for the main content area
 * - Topic workspace (replaces main content when a topic is focused)
 *
 * Pages can use PageLayout for additional layout features like:
 * - Secondary panels
 * - Right panels (overlay/drawer)
 * - Content width variants
 *
 * The Layout now defaults to full-height mode, letting individual
 * pages control their content width and padding via PageLayout.
 */

import { type ReactNode, Component, lazy, Suspense, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { StatusBanner } from '../StatusBanner';
import { MaintenanceBanner } from '../status/MaintenanceBanner';
import { GlobalChatSidebar } from '../chat/GlobalChatSidebar';
import { GlobalSearchDialog } from '../search/GlobalSearchDialog';
import { ChatSelectionProvider } from '../../contexts/ChatSelectionContext';
import { ChatSessionProvider } from '../../contexts/ChatSessionContext';
import { RightPanelRegistryProvider } from '../../contexts/RightPanelRegistryContext';
import { TopicBarProvider, useTopicBar } from '../../contexts/TopicBarContext';
import { TopicBar } from '../topics/TopicBar';

const TopicWorkspace = lazy(() => import('../topics/TopicWorkspace'));

/** Error boundary for TopicWorkspace — prevents crashes from taking down the whole app */
class TopicErrorBoundary extends Component<
  { children: ReactNode; onReset?: () => void },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
          <div style={{ fontSize: 14, fontWeight: 600, color: '#292524' }}>Topic workspace encountered an error</div>
          <div style={{ fontSize: 12, color: '#78716c', maxWidth: 400 }}>{this.state.error.message}</div>
          <button
            onClick={() => { this.setState({ error: null }); this.props.onReset?.(); }}
            className="px-4 py-2 rounded-lg text-white"
            style={{ fontSize: 12, background: '#f97066' }}
          >
            Reload workspace
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

interface LayoutProps {
  children: ReactNode;
}

/** Inner layout that can read topic bar context */
function LayoutInner({ children }: LayoutProps) {
  const { focusedTopicId, focusedTopic } = useTopicBar();
  const hasFocusedTopic = !!focusedTopicId && !!focusedTopic;
  const location = useLocation();

  // Sidebar overlay in immersive topic mode
  const [sidebarOverlay, setSidebarOverlay] = useState(false);
  // Close sidebar overlay when leaving immersive mode
  useEffect(() => {
    if (!hasFocusedTopic) setSidebarOverlay(false);
  }, [hasFocusedTopic]);

  // Global search + sidebar toggle (⌘K / ⌘B) — available in immersive topic mode
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  useEffect(() => {
    if (!hasFocusedTopic) return; // Sidebar handles its own shortcuts when mounted
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(prev => !prev);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        setSidebarOverlay(prev => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasFocusedTopic]);

  // Pop-out topic pages render without sidebar/topicbar
  const isPopout = location.pathname.startsWith('/topic/');
  if (isPopout) {
    return (
      <div className="h-screen flex flex-col bg-surface-muted overflow-hidden">
        {children}
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-surface-muted overflow-hidden">
      {/* Topic Bar — always visible at top */}
      <TopicBar onToggleSidebar={hasFocusedTopic ? () => setSidebarOverlay(prev => !prev) : undefined} />

      {/* Main row: sidebar + content */}
      <div className="flex-1 flex min-h-0 overflow-x-hidden relative">
        {/* Global Sidebar — always shown when no topic focused */}
        {!hasFocusedTopic && <Sidebar />}

        {/* Sidebar overlay in immersive topic mode — slides in from left */}
        {hasFocusedTopic && sidebarOverlay && (
          <>
            {/* Backdrop — only covers sidebar width so main content stays clickable */}
            <div
              className="absolute inset-0 z-40 pointer-events-none"
              style={{ background: 'rgba(0,0,0,0.15)' }}
              onClick={() => setSidebarOverlay(false)}
            >
              {/* Only the backdrop area (sidebar width) intercepts clicks */}
              <div className="absolute inset-0 pointer-events-auto" onClick={() => setSidebarOverlay(false)} style={{ width: 260 }} />
            </div>
            {/* Sidebar */}
            <div
              className="absolute left-0 top-0 bottom-0 z-50"
              style={{
                width: 260,
                boxShadow: '4px 0 24px rgba(0,0,0,0.08)',
                animation: 'slideInLeft 200ms ease-out',
              }}
            >
              <Sidebar />
            </div>
            <style>{`
              @keyframes slideInLeft {
                from { transform: translateX(-100%); opacity: 0.5; }
                to { transform: translateX(0); opacity: 1; }
              }
            `}</style>
          </>
        )}

        {/* Main Content Area or Topic Workspace */}
        {hasFocusedTopic ? (
          <TopicErrorBoundary key={focusedTopicId}>
            <Suspense fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-coral-400 border-t-transparent rounded-full animate-spin" />
              </div>
            }>
              <TopicWorkspace key={focusedTopicId} topic={focusedTopic} />
            </Suspense>
          </TopicErrorBoundary>
        ) : (
          <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
            {/* Status banner — shown when active incidents exist */}
            <StatusBanner />
            {/* Maintenance banner — shown 24h before scheduled maintenance until end */}
            <MaintenanceBanner />
            {children}
          </main>
        )}

        {/* Martha Chat Sidebar — hidden when a topic is focused (topics have their own chat) */}
        {!hasFocusedTopic && <GlobalChatSidebar />}
      </div>

      {/* Global search — rendered at layout level when sidebar is unmounted */}
      {hasFocusedTopic && (
        <GlobalSearchDialog isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      )}
    </div>
  );
}

const Layout = ({ children }: LayoutProps) => {
  return (
    <ChatSelectionProvider>
      <ChatSessionProvider>
        <RightPanelRegistryProvider>
          <TopicBarProvider>
            <LayoutInner>{children}</LayoutInner>
          </TopicBarProvider>
        </RightPanelRegistryProvider>
      </ChatSessionProvider>
    </ChatSelectionProvider>
  );
};

export default Layout;
